import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Tab, { tabClasses } from "@mui/material/Tab";
import Tabs, { tabsClasses, type TabsProps } from "@mui/material/Tabs";
import type { SxProps, Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  recallPath,
  rememberPath,
  resolveActive,
  searchEntries,
  visibleModes,
  type AccessEvaluator,
  type NavDefinition,
  type NavMode,
  type NavPage,
  type NavSearchEntry,
} from "../../navigation";
import { ModeSearchDialog } from "./ModeSearchDialog";
import {
  PhoneModeSwitcher,
  PhonePageChips,
  type PhoneSheetAction,
} from "./PhoneModeSwitcher";
import { navigationStorage } from "./storage";
import { SHELL_BAR, SHELL_SURFACE, shellAccent, shellColors, type ShellAppearance } from "./tokens";

type SlotPropsIndicator = NonNullable<TabsProps["slotProps"]>["indicator"];

/** Same breakpoint as the Material `expanded` layout class. */
export const MODE_SHELL_EXPANDED_QUERY = "(min-width:840px)";

export interface ModeShellProps<A> {
  definition: NavDefinition<A>;
  allow: AccessEvaluator<A>;
  /** False while permission hints load: no mode or page items render. */
  ready: boolean;
  /** Per-user memory namespace, e.g. `${app}:${userId}`. */
  storageKey: string;
  /** Left slot (logo; business adds the facility switcher). */
  brand: ReactNode;
  /** Right slot (bell, environment chip, avatar/user menu). */
  trailing: ReactNode;
  /** Renders the visible `inUserMenu` pages (e.g. My coaching) after `trailing`. */
  userMenuPages?: (pages: NavPage<A>[]) => ReactNode;
  /** Optional per-page badge, e.g. the queue count on Drop-in live. */
  badge?: (pageId: string) => ReactNode;
  /** Extra styles for the routed `main` element. */
  mainSx?: SxProps<Theme>;
  /** Mode tabs: business underlines the active tab; platform uses pills. */
  tabVariant?: "underline" | "pill";
  /** False renders a text-only page sub-menu (platform). */
  showPageIcons?: boolean;
  /** Section tabs: an underline row (business) or a segmented control (platform). */
  sectionVariant?: "underline" | "segmented";
  /** Business or platform variant of the chrome (scrims, search selection). */
  appearance?: ShellAppearance;
  /** Phone switcher: a bottom sheet (business) or a card under the bar (platform). */
  phoneSwitcher?: "sheet" | "dropdown";
  /** Extra actions in the phone switcher's footer, e.g. Switch facility. */
  phoneSheetActions?: readonly PhoneSheetAction[];
  /**
   * The person's start page. On a page outside their menu (e.g. no access)
   * the phone switcher names the mode of this page instead of "Menu".
   */
  homePath?: string | null;
  /** Width of the desktop search trigger in px. */
  searchWidth?: number;
  children: ReactNode;
}

function splitHref(href: string): [string, string] {
  const index = href.indexOf("?");
  return index === -1 ? [href, ""] : [href.slice(0, index), href.slice(index)];
}

function menuPages<A>(mode: NavMode<A>): NavMode<A> {
  return { ...mode, pages: mode.pages.filter((page) => !page.inUserMenu) };
}

function isPlainLeftClick(event: MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

function SkipLink() {
  return (
    <ButtonBase
      component="a"
      href="#main-content"
      sx={(theme) => ({
        position: "fixed",
        top: -80,
        left: 8,
        zIndex: theme.zIndex.modal + 1,
        borderRadius: 1,
        bgcolor: "background.paper",
        ...shellAccent(theme, "color"),
        px: 2,
        py: 1,
        fontWeight: 700,
        textDecoration: "none",
        "&:focus": { top: 8 },
      })}
    >
      Skip to main content
    </ButtonBase>
  );
}

function SearchTrigger({
  onOpen,
  width,
  raised,
}: {
  onOpen: () => void;
  width: number;
  /** Platform's field: 32px, slate-800 fill (mockup `.tb-search`). */
  raised: boolean;
}) {
  // The hit area keeps the theme's touch target; the visible field is the
  // mockup's 30px (business) or 32px (platform) search box inside it.
  return (
    <ButtonBase
      type="button"
      aria-label="Search"
      aria-keyshortcuts="Meta+K Control+K"
      onClick={onOpen}
      sx={{
        flexShrink: 0,
        borderRadius: "7px",
        color: SHELL_BAR.muted,
        "&:hover .shell-search-field": { bgcolor: SHELL_BAR.hover },
        "&:focus-visible": { outline: "none" },
        "&:focus-visible .shell-search-field": {
          outline: `2px solid ${SHELL_BAR.accent}`,
          outlineOffset: 1,
        },
      }}
    >
      <Box
        component="span"
        className="shell-search-field"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: { xs: 32, lg: width },
          height: raised ? 32 : 30,
          px: raised ? 1.25 : 1.125,
          justifyContent: { xs: "center", lg: "flex-start" },
          borderRadius: "7px",
          border: `1px solid ${raised ? SHELL_BAR.fieldRaisedBorder : SHELL_BAR.hover}`,
          bgcolor: raised ? SHELL_BAR.fieldRaised : SHELL_BAR.field,
          fontSize: raised ? 13 : 12.5,
        }}
      >
        <SearchOutlinedIcon sx={{ fontSize: raised ? 16 : 18 }} aria-hidden="true" />
        <Box
          component="span"
          sx={{ display: { xs: "none", lg: "inline" }, flex: 1, textAlign: "left" }}
        >
          Search or jump to
        </Box>
        <Box
          component="kbd"
          sx={{
            display: { xs: "none", lg: "inline-flex" },
            px: 0.625,
            border: "1px solid rgba(255,255,255,0.16)",
            borderRadius: "4px",
            fontFamily: "inherit",
            fontSize: 10.5,
            lineHeight: 1.3,
            color: SHELL_BAR.muted,
          }}
        >
          ⌘K
        </Box>
      </Box>
    </ButtonBase>
  );
}

function modeTabSx(
  active: boolean,
  iconOnly: boolean,
  variant: "underline" | "pill",
): SxProps<Theme> {
  const focus = {
    "&:focus-visible": {
      outline: `2px solid ${SHELL_BAR.accent}`,
      outlineOffset: variant === "pill" ? 1 : -2,
    },
  };
  if (variant === "pill") {
    // Platform (mockup `.modes a`, `.home`): 32px pills, slate-800 when active.
    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "7px",
      flexShrink: 0,
      height: 32,
      minWidth: 32,
      px: iconOnly ? 0 : 1.5,
      borderRadius: "7px",
      whiteSpace: "nowrap",
      bgcolor: active ? SHELL_BAR.raised : "transparent",
      color: active
        ? iconOnly
          ? SHELL_BAR.accentSoft
          : SHELL_BAR.strong
        : iconOnly
          ? SHELL_BAR.muted
          : SHELL_BAR.foreground,
      fontSize: 13.5,
      fontWeight: 500,
      textDecoration: "none",
      "&:hover": {
        bgcolor: active ? SHELL_BAR.raised : SHELL_BAR.hover,
        color: active && iconOnly ? SHELL_BAR.accentSoft : SHELL_BAR.strong,
      },
      ...focus,
    };
  }
  return {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: 1.5,
    flexShrink: 0,
    whiteSpace: "nowrap",
    color: active ? SHELL_BAR.strong : SHELL_BAR.muted,
    fontSize: 13.5,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { color: SHELL_BAR.strong },
    ...focus,
    ...(active
      ? {
          "&::after": {
            content: '""',
            position: "absolute",
            left: 10,
            right: 10,
            bottom: 0,
            height: 2,
            borderRadius: "2px",
            bgcolor: SHELL_BAR.accent,
          },
        }
      : {}),
  };
}

function ModeTabs<A>({
  modes,
  activeModeId,
  onSelect,
  storageKey,
  variant,
}: {
  modes: readonly NavMode<A>[];
  activeModeId: string | null;
  onSelect: (mode: NavMode<A>) => void;
  storageKey: string;
  variant: "underline" | "pill";
}) {
  return (
    <Box
      component="nav"
      aria-label="Modes"
      sx={{
        display: "flex",
        alignSelf: "stretch",
        alignItems: variant === "pill" ? "center" : "stretch",
        gap: "2px",
        ml: 1.25,
        minWidth: 0,
        overflowX: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {modes.map((mode) => {
        const active = mode.id === activeModeId;
        const ModeIcon = mode.icon;
        const iconOnly = mode.iconOnly === true;
        return (
          <Box
            key={mode.id}
            component={Link}
            to={recallPath(navigationStorage(), storageKey, mode)}
            // The page link below is the current page; the mode tab is only
            // the current item of its own set (as in PhoneModeSwitcher).
            aria-current={active ? "true" : undefined}
            aria-label={iconOnly ? mode.label : undefined}
            title={iconOnly ? mode.label : undefined}
            onClick={(event: MouseEvent) => {
              if (!isPlainLeftClick(event)) return;
              event.preventDefault();
              onSelect(mode);
            }}
            sx={modeTabSx(active, iconOnly, variant)}
          >
            {iconOnly ? (
              <Box component="span" aria-hidden="true" sx={{ display: "inline-flex", fontSize: 20 }}>
                <ModeIcon fontSize="inherit" />
              </Box>
            ) : (
              mode.label
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function PageMenu<A>({
  mode,
  activePageId,
  badge,
  showIcons,
}: {
  mode: NavMode<A>;
  activePageId: string | null;
  badge?: (pageId: string) => ReactNode;
  showIcons: boolean;
}) {
  return (
    <Box
      component="nav"
      aria-label={`${mode.label} pages`}
      sx={(theme) => ({
        display: "flex",
        alignItems: "stretch",
        gap: showIcons ? "2px" : "4px",
        height: 38,
        flexShrink: 0,
        px: showIcons ? 1.5 : 1.75,
        borderBottom: 1,
        ...shellColors(theme, { backgroundColor: "surface", borderColor: "line" }),
        overflowX: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      })}
    >
      {mode.pages.map((page) => {
        const active = page.id === activePageId;
        const PageIcon = page.icon;
        return (
          <Box
            key={page.id}
            component={Link}
            to={page.path}
            aria-current={active ? "page" : undefined}
            sx={(theme) => ({
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              flexShrink: 0,
              whiteSpace: "nowrap",
              fontSize: 13,
              // Business bolds the current page (mockup `.subb`); platform's
              // text-only menu keeps one weight (mockup `.subnav`).
              fontWeight: active && showIcons ? 600 : 500,
              textDecoration: "none",
              ...shellColors(theme, { color: active ? "ink" : "ink3" }),
              "& .shell-page-icon": {
                display: "inline-flex",
                fontSize: 16,
                ...(active ? shellAccent(theme, "color") : shellColors(theme, { color: "muted2" })),
              },
              "&:hover": shellColors(theme, { color: "ink" }),
              "&:focus-visible": {
                outline: 2,
                ...shellAccent(theme, "outlineColor"),
                outlineOffset: -2,
              },
              "&::after": active
                ? {
                    content: '""',
                    position: "absolute",
                    left: showIcons ? 8 : 0,
                    right: showIcons ? 8 : 0,
                    bottom: -1,
                    height: 2,
                    ...shellAccent(theme, "backgroundColor"),
                  }
                : undefined,
            })}
          >
            {showIcons ? (
              <Box component="span" className="shell-page-icon" aria-hidden="true">
                <PageIcon fontSize="inherit" />
              </Box>
            ) : null}
            <span>{page.label}</span>
            {badge?.(page.id)}
          </Box>
        );
      })}
    </Box>
  );
}

function sectionTabsSx(variant: "underline" | "segmented") {
  return (theme: Theme) =>
    variant === "segmented"
      ? {
          // Platform (mockup `.seg`): a slate track with a raised white segment.
          display: "inline-flex",
          maxWidth: "100%",
          minHeight: 0,
          p: "2px",
          borderRadius: "7px",
          ...shellColors(theme, { backgroundColor: "fill" }),
          [`& .${tabsClasses.list}`]: { gap: "2px" },
          [`& .${tabsClasses.indicator}`]: { display: "none" },
        }
      : { minHeight: 36 };
}

function sectionTabSx(variant: "underline" | "segmented") {
  return (theme: Theme) => {
    const focus = {
      "&.Mui-focusVisible": {
        outline: 2,
        ...shellAccent(theme, "outlineColor"),
        outlineOffset: variant === "segmented" ? 0 : -2,
      },
    };
    if (variant === "segmented") {
      return {
        minWidth: 0,
        minHeight: 26,
        height: 26,
        px: 1.25,
        py: 0,
        borderRadius: "5px",
        fontSize: 12.5,
        fontWeight: 500,
        textTransform: "none",
        opacity: 1,
        ...shellColors(theme, { color: "ink3" }),
        [`&.${tabClasses.selected}`]: {
          boxShadow: SHELL_SURFACE.segmentShadow,
          ...shellColors(theme, { color: "ink", backgroundColor: "surface" }),
        },
        ...focus,
      };
    }
    return {
      minWidth: 0,
      minHeight: 36,
      px: 1.25,
      py: 0,
      fontSize: 13,
      fontWeight: 500,
      textTransform: "none",
      opacity: 1,
      ...shellColors(theme, { color: "muted" }),
      [`&.${tabClasses.selected}`]: {
        fontWeight: 600,
        ...shellColors(theme, { color: "ink" }),
      },
      ...focus,
    };
  };
}

function SectionTabs<A>({
  page,
  activeSectionId,
  variant,
}: {
  page: NavPage<A>;
  activeSectionId: string | null;
  variant: "underline" | "segmented";
}) {
  const sections = page.sections ?? [];
  const segmented = variant === "segmented";
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        minWidth: 0,
        height: segmented ? 48 : undefined,
        px: segmented ? 2 : { xs: 0.5, sm: 1.5 },
        borderBottom: 1,
        ...shellColors(theme, { backgroundColor: "surface", borderColor: "line" }),
      })}
    >
      <Tabs
        value={activeSectionId ?? false}
        aria-label={`${page.label} sections`}
        variant="scrollable"
        // The segmented track scrolls by touch; arrow buttons would sit inside it.
        scrollButtons={segmented ? false : "auto"}
        allowScrollButtonsMobile={!segmented}
        textColor="inherit"
        slotProps={{
          indicator: {
            "data-testid": "shell-section-indicator",
            // The underline sits 8px inside each tab edge (mockup `.tabs`).
            sx: (theme: Theme) => ({
              clipPath: "inset(0 8px)",
              ...shellAccent(theme, "backgroundColor"),
            }),
          } as SlotPropsIndicator,
        }}
        sx={sectionTabsSx(variant)}
      >
        {sections.map((section) => (
          <Tab
            key={section.id}
            component={Link}
            to={section.path}
            value={section.id}
            label={section.label}
            sx={sectionTabSx(variant)}
          />
        ))}
      </Tabs>
    </Box>
  );
}

/**
 * Staff modes shell (direction B): a top bar with the user's modes, a slim
 * sub-menu row of the active mode's pages, ⌘K search and, below 840px, a mode
 * switcher with a page chip row. The active mode always comes from the URL.
 */
export function ModeShell<A>({
  definition,
  allow,
  ready,
  storageKey,
  brand,
  trailing,
  userMenuPages,
  badge,
  mainSx,
  tabVariant = "underline",
  showPageIcons = true,
  sectionVariant = "underline",
  appearance = "business",
  phoneSwitcher = "sheet",
  phoneSheetActions,
  homePath,
  searchWidth = 300,
  children,
}: ModeShellProps<A>) {
  const expanded = useMediaQuery(MODE_SHELL_EXPANDED_QUERY);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);

  const modes = useMemo(
    () => (ready ? visibleModes(definition, allow) : []),
    [allow, definition, ready],
  );
  /** Modes with at least one sub-menu page get a tab; user-menu-only modes don't. */
  const tabModes = useMemo(
    () => modes.map(menuPages).filter((mode) => mode.pages.length > 0),
    [modes],
  );
  const active = resolveActive(modes, location.pathname, location.search);
  const activeModeId = active?.mode.id ?? null;
  const activePageId = active?.page.id ?? null;
  const activeSectionId = active?.section?.id ?? null;
  const showSections = (active?.page.sections?.length ?? 0) > 1;
  // Remember the page or section, never the detail URL under it: a record id
  // belongs to one organization and facility and must not be replayed.
  const memoryPath =
    active && !active.page.inUserMenu ? (active.section?.path ?? active.page.path) : null;

  useEffect(() => {
    if (!memoryPath || !activeModeId) return;
    rememberPath(navigationStorage(), storageKey, activeModeId, memoryPath);
  }, [activeModeId, memoryPath, storageKey]);

  useEffect(() => {
    const toggleSearch = (event: KeyboardEvent) => {
      if (
        event.key.toLocaleLowerCase() === "k" &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        setSearchOpen((current) => !current);
      }
    };
    document.addEventListener("keydown", toggleSearch);
    return () => document.removeEventListener("keydown", toggleSearch);
  }, []);

  const goToMode = useCallback(
    (mode: NavMode<A>) => {
      navigate(recallPath(navigationStorage(), storageKey, menuPages(mode)));
    },
    [navigate, storageKey],
  );

  const entries = useMemo(() => searchEntries(modes), [modes]);
  const selectEntry = useCallback(
    (entry: NavSearchEntry) => {
      setSearchOpen(false);
      navigate(entry.path);
    },
    [navigate],
  );

  const userPages = modes.flatMap((mode) =>
    mode.pages.filter((page) => page.inUserMenu),
  );
  const showModeTabs = tabModes.length > 1;
  const homeModeId = homePath
    ? (resolveActive(tabModes, ...splitHref(homePath))?.mode.id ?? null)
    : null;
  // With a single mode its pages are the whole menu, wherever the user is.
  const subMenuMode =
    tabModes.find((mode) => mode.id === activeModeId) ??
    (tabModes.length === 1 ? tabModes[0] : null);
  const showSubMenu = subMenuMode !== null && subMenuMode.pages.length > 1;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        bgcolor: "background.default",
      }}
    >
      <SkipLink />
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Box
          component="header"
          // The bar is always dark: scope the theme's dark scheme to it.
          data-bw-color-scheme="dark"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: expanded ? 1.25 : 0.75,
            height: expanded ? 48 : 52,
            pl: expanded ? 1.75 : 1.5,
            pr: expanded ? 1.75 : 1,
            bgcolor: SHELL_BAR.background,
            color: SHELL_BAR.foreground,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {!expanded && showModeTabs ? (
            <PhoneModeSwitcher
              modes={tabModes}
              activeModeId={activeModeId}
              onSelectMode={goToMode}
              userPages={userPages}
              actions={phoneSheetActions}
              fallbackModeId={homeModeId}
              variant={phoneSwitcher}
              appearance={appearance}
            />
          ) : null}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexShrink: 1 }}>
            {brand}
          </Box>
          {expanded && showModeTabs ? (
            <ModeTabs
              modes={tabModes}
              activeModeId={activeModeId}
              onSelect={goToMode}
              storageKey={storageKey}
              variant={tabVariant}
            />
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }} />
          {expanded ? (
            <SearchTrigger
              onOpen={() => setSearchOpen(true)}
              width={searchWidth}
              raised={tabVariant === "pill"}
            />
          ) : (
            <IconButton
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
              sx={{ color: SHELL_BAR.foreground, width: 36, height: 36 }}
            >
              <SearchOutlinedIcon aria-hidden="true" sx={{ fontSize: 21 }} />
            </IconButton>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            {trailing}
            {userMenuPages ? userMenuPages(ready ? userPages : []) : null}
          </Box>
        </Box>
        {showSubMenu && subMenuMode ? (
          expanded ? (
            <PageMenu
              mode={subMenuMode}
              activePageId={activePageId}
              badge={badge}
              showIcons={showPageIcons}
            />
          ) : (
            <PhonePageChips mode={subMenuMode} activePageId={activePageId} badge={badge} />
          )
        ) : null}
        {showSections && active ? (
          <SectionTabs
            page={active.page}
            activeSectionId={activeSectionId}
            variant={sectionVariant}
          />
        ) : null}
      </Box>
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={[
          {
            display: "flex",
            flex: 1,
            minWidth: 0,
            width: "100%",
            flexDirection: "column",
            outline: "none",
          },
          ...(Array.isArray(mainSx) ? mainSx : mainSx ? [mainSx] : []),
        ]}
      >
        {children}
      </Box>
      <ModeSearchDialog
        open={searchOpen}
        entries={entries}
        onClose={() => setSearchOpen(false)}
        onSelect={selectEntry}
        appearance={appearance}
      />
    </Box>
  );
}
